import React, { useContext, useMemo, useState } from 'react'
import { Card } from 'primereact/card'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Skeleton } from 'primereact/skeleton'
import { Calendar } from 'primereact/calendar'
import { Dropdown } from 'primereact/dropdown'
import { Button } from 'primereact/button'
import { Toast } from 'primereact/toast'
import { Tag } from 'primereact/tag'
import { Dialog } from 'primereact/dialog'
import { useRef, useEffect } from 'react'
import { getAllZones } from '../../api/axiosZones'
import { getZoneRegions } from '../../api/axiosRegions'
import { getRegionDistricts } from '../../api/axiosDistricts'
import { getAllIncidentStatuses } from '../../api/axiosIncidentStatuses'
import { Paginator } from 'primereact/paginator'
import { Divider } from 'primereact/divider'
import DataContext from '../../context/DataContext'
import { exportCommentsTable, getFollowupsReportTable } from '../../api/axiosComments'
import { useTranslation } from 'react-i18next'
import IncidentViewForm from '../../components/incident/IncidentViewForm'
import { getOneIncident } from '../../api/axiosIncidents'

const FollowupsTable = () => {
    const { t } = useTranslation();
    const { setFetcherror } = useContext(DataContext);

    const toast = useRef(null);

    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [exportingPDF, setExportingPDF] = useState(false);
    const [exportingExcel, setExportingExcel] = useState(false);
    const [exportingCSV, setExportingCSV] = useState(false);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const limitOptions = useMemo(() => ([10, 20, 50, 100]), []);

    const [zones, setZones] = useState([]);
    const [selectedZone, setSelectedZone] = useState("");
    const [regions, setRegions] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState("");
    const [districts, setDistricts] = useState([]);
    const [selectedDistrict, setSelectedDistrict] = useState("");

    const [statuses, setStatuses] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState("");

    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [viewDialogVisible, setViewDialogVisible] = useState(false);
    const [ICNrowData, setICNrowData] = useState(null);
    const [incidentValidity, setIncidentValidity] = useState(false);
    const [appliedFilters, setAppliedFilters] = useState({});

    const skeletonitems = useMemo(() => Array.from({ length: 8 }).map((_, i) => ({ id: i })), []);
    const compactDropdownClass = "w-full";
    const compactControlStyle = { height: "32px", fontSize: "12px" };
    const compactInputStyle = { fontSize: "12px", padding: "0.35rem 0.5rem" };
    const compactButtonClass = "p-button-sm py-2 px-3 text-xs";

    const truncate = (s, n = 70) => {
        if (!s) return "";
        const str = String(s);
        return str.length > n ? `${str.slice(0, n)}...` : str;
    };

    const hashString = (s) => {
        const str = (s || "").toString();
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = ((hash << 5) - hash) + str.charCodeAt(i);
            hash |= 0;
        }
        return Math.abs(hash);
    };

    // color palette from App.css (.lhrcCOLORS)
    const statusColorPalette = [
        '#41A6D7', // blue
        '#E85E3F', // orange/red
        '#CFE072', // lime
        '#91D3CA', // mint
        '#1E8C8A', // teal
        '#3E5B58', // dark green/gray
        '#1E4376', // deep blue
        '#143968', // navy
    ];

    const getReadableTextColor = (hex) => {
        const c = hex.replace('#', '');
        const r = parseInt(c.substring(0, 2), 16) / 255;
        const g = parseInt(c.substring(2, 4), 16) / 255;
        const b = parseInt(c.substring(4, 6), 16) / 255;
        const luminance = (0.2126 * r + 0.7152 * g + 0.0722 * b);
        return luminance > 0.62 ? '#0f172a' : '#ffffff';
    };

    const getStatusTagStyle = (statusName) => {
        const statusObj = statuses?.find((s) => s?.name === statusName);
        const stableKey = statusObj?.createdAt || statusObj?.id || statusName || "unknown";
        const idx = hashString(stableKey) % statusColorPalette.length;
        const bg = statusColorPalette[idx];
        return {
            backgroundColor: bg,
            color: getReadableTextColor(bg),
            border: '1px solid rgba(0,0,0,0.08)',
            fontSize: '11px',
            fontWeight: 600
        };
    };

    const incidentSnapshotBody = (rowData) => {
        const inc = rowData?.incident;
        if (!inc) return "";
        const victim = `${inc.firstnamevictim || ""} ${inc.middlenamevictim || ""} ${inc.lastnamevictim || ""}`.replace(/\s+/g, " ").trim();

        const toTitleCase = (s) => {
            if (!s) return "";
            return String(s)
                .toLowerCase()
                .split(" ")
                .filter(Boolean)
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(" ");
        };

        const region = (inc.region || "").toString().toUpperCase();
        const district = toTitleCase(inc.district);
        return (
            <div>
                <div className='font-medium' style={{ color: '#0f172a', marginBottom: '0.35rem' }}>
                    {inc.incidentnumber || ""}
                </div>
                <div className='text-color-secondary text-sm'>
                    <i className="pi pi-user" style={{ fontSize: '0.75rem', marginRight: '0.4rem', color: '#0ea5e9' }}></i>
                    {victim}
                </div>
                <div className='text-color-secondary text-sm'>
                    <i className="pi pi-align-left" style={{ fontSize: '0.75rem', marginRight: '0.4rem', color: '#f97316' }}></i>
                    {truncate(inc.eventdescription, 80)}
                </div>
                <div className='text-color-secondary text-xs' style={{ marginTop: '0.25rem' }}>
                    <i className="pi pi-map-marker" style={{ fontSize: '0.75rem', marginRight: '0.4rem', color: '#22c55e' }}></i>
                    <span>{region}</span>
                    {district ? <span> - <span style={{ fontWeight: 700 }}>{district}</span></span> : null}
                </div>
            </div>
        );
    };

    const userBody = (rowData) => {
        const u = rowData?.user;
        if (u) return `${u.firstname || ""} ${u.lastname || ""}`.trim();
        return rowData?.fromname || "";
    };

    const dateBody = (rowData) => {
        const d = rowData?.createdAt ? new Date(rowData.createdAt) : null;
        return d ? d.toLocaleString() : "";
    };

    const snBody = (rowData, options) => {
        return <span className="font-medium text-700">{first + (options.rowIndex + 1)}</span>;
    };

    const followupAndStatusBody = (rowData) => {
        const statusName = rowData?.status || "";
        return (
            <div>
                <div style={{ fontWeight: 600, color: '#0f172a' }}>
                    {truncate(rowData?.content, 140)}
                </div>
                <div className="mt-2">
                    <Tag value={statusName || t("Status")} style={getStatusTagStyle(statusName)} />
                </div>
            </div>
        );
    };

    const userAndDateBody = (rowData) => {
        return (
            <div>
                <div className='text-sm' style={{ fontWeight: 600, color: '#0f172a' }}>
                    <i className="pi pi-user" style={{ fontSize: '0.8rem', marginRight: '0.4rem', color: '#0ea5e9' }}></i>
                    {userBody(rowData)}
                </div>
                <div className='text-xs text-color-secondary mt-1'>
                    <i className="pi pi-calendar" style={{ fontSize: '0.8rem', marginRight: '0.4rem', color: '#22c55e' }}></i>
                    {dateBody(rowData)}
                </div>
            </div>
        );
    };

    const viewBody = (rowData) => (
        <Button
            outlined
            label={t("View")}
            icon="pi pi-eye"
            className="p-button-success px-3 py-1 text-xs mb-2 font-light"
            onClick={async () => {
                const iid = rowData?.incident?.id || rowData?.incident_id;
                if (!iid) return;
                await getOneIncident(iid, setFetcherror, (inc) => {
                    setICNrowData(inc);
                    setIncidentValidity(!!inc?.isvalid);
                    setViewDialogVisible(true);
                });
            }}
        />
    );

    const formatStartDate = (d) => {
        if (!d) return "";
        return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
    }

    const formatEndDate = (d) => {
        if (!d) return "";
        return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate() + 1) - 1).toISOString();
    }

    const handleGenerate = async () => {
        setLoading(true);
        setFirst(0);

        try {
            const filters = {
                zone: selectedZone?.name,
                region: selectedRegion?.name,
                district: selectedDistrict?.name,
                status: selectedStatus?.name,
                startdate: formatStartDate(startDate),
                enddate: formatEndDate(endDate)
            };
            setAppliedFilters(filters);
            await getFollowupsReportTable(
                setComments,
                setFetcherror,
                () => {},
                setFirst,
                setRows,
                { first: 0, rows },
                setTotalCount,
                filters
            );

            toast.current?.show({
                severity: 'success',
                summary: 'Success',
                detail: 'Report generated successfully',
                life: 2500
            });
        } catch (e) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Could not generate report',
                life: 4000
            });
        } finally {
            setLoading(false);
        }
    };

    const onPageChange = async (event) => {
        setLoading(true);
        const filters = {
            zone: selectedZone?.name,
            region: selectedRegion?.name,
            district: selectedDistrict?.name,
            status: selectedStatus?.name,
            startdate: formatStartDate(startDate),
            enddate: formatEndDate(endDate)
        };
        setAppliedFilters(filters);

        await getFollowupsReportTable(
            setComments,
            setFetcherror,
            () => {},
            setFirst,
            setRows,
            event,
            setTotalCount,
            filters
        );
        setLoading(false);
    };

    const handleExport = async (type) => {
        if (type === 'pdf') setExportingPDF(true);
        else if (type === 'xlsx' || type === 'excel') setExportingExcel(true);
        else if (type === 'csv') setExportingCSV(true);

        try {
            await exportCommentsTable(appliedFilters || {}, type);
        } finally {
            setExportingPDF(false);
            setExportingExcel(false);
            setExportingCSV(false);
        }
    };

    useEffect(() => {
        getAllZones(setZones, setFetcherror);
        getAllIncidentStatuses(setStatuses, setFetcherror);
    }, []);

    return (
        <div>
            <Toast ref={toast} />
            <Card className="m-2">
                <div className='flex justify-content-end gap-2'>
                    <Button icon="pi pi-file-pdf" tooltip="Export as PDF" tooltipOptions={{ position: 'bottom' }} severity="danger" text className="py-0 px-2"
                        loading={exportingPDF} disabled={!comments || comments.length === 0 || exportingExcel || exportingCSV} onClick={() => handleExport('pdf')} />
                    <Button icon="pi pi-file-excel" tooltip="Export as Excel" tooltipOptions={{ position: 'bottom' }} severity="success" text className="py-0 px-2"
                        loading={exportingExcel} disabled={!comments || comments.length === 0 || exportingPDF || exportingCSV} onClick={() => handleExport('xlsx')} />
                    <Button icon="pi pi-file" tooltip="Export as CSV" tooltipOptions={{ position: 'bottom' }} severity="info" text className="py-0 px-2"
                        loading={exportingCSV} disabled={!comments || comments.length === 0 || exportingPDF || exportingExcel} onClick={() => handleExport('csv')} />
                </div>

                <div className='my-4'>
                    <Divider align="start" type='solid' style={{ color: "black", width: '100%' }}>
                        <span className="p-tag text-base mx-2 bg-white text-primary">
                            <i className="pi pi-chart-bar" style={{ fontSize: '0.7rem', marginRight: '0.5rem' }}></i>
                            {t("FOLLOW-UPS REPORT")}
                        </span>
                    </Divider>
                </div>

                <div className='flex flex-column md:flex-row justify-content-between mb-4 w-full gap-3'>
                    <div className='flex flex-column md:flex-row justify-content-center w-full gap-2'>
                        <div className="flex-auto">
                            <Dropdown
                                value={selectedZone}
                                onChange={(e) => {
                                    setSelectedZone(e.value);
                                    setSelectedRegion("");
                                    setSelectedDistrict("");
                                    setRegions([]);
                                    setDistricts([]);
                                    if (e.value?.id) getZoneRegions(setRegions, setFetcherror, e.value?.id);
                                }}
                                options={zones}
                                optionLabel="name"
                                placeholder={t("Select Zone")}
                                showClear
                                className={compactDropdownClass}
                                style={compactControlStyle}
                                inputStyle={compactInputStyle}
                            />
                        </div>

                        <div className="flex-auto">
                            <Dropdown
                                value={selectedRegion}
                                onChange={(e) => {
                                    setSelectedRegion(e.value);
                                    setSelectedDistrict("");
                                    setDistricts([]);
                                    if (e.value?.id) getRegionDistricts(setDistricts, setFetcherror, e.value?.id);
                                }}
                                options={regions}
                                optionLabel="name"
                                placeholder={t("Select Region")}
                                showClear
                                className={compactDropdownClass}
                                style={compactControlStyle}
                                inputStyle={compactInputStyle}
                            />
                        </div>

                        <div className="flex-auto">
                            <Dropdown
                                value={selectedDistrict}
                                onChange={(e) => setSelectedDistrict(e.value)}
                                options={districts}
                                optionLabel="name"
                                placeholder={t("Select District")}
                                showClear
                                className={compactDropdownClass}
                                style={compactControlStyle}
                                inputStyle={compactInputStyle}
                            />
                        </div>

                        <div className="flex-auto">
                            <Dropdown
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.value)}
                                options={statuses}
                                optionLabel="name"
                                placeholder={t("Select Status")}
                                showClear
                                className={compactDropdownClass}
                                filter
                                style={compactControlStyle}
                                inputStyle={compactInputStyle}
                            />
                        </div>

                        <div className="flex-auto">
                            <Calendar
                                value={startDate}
                                onChange={(e) => setStartDate(e.value)}
                                className="w-full"
                                style={compactControlStyle}
                                inputStyle={compactInputStyle}
                                placeholder={t("Select Start Date")}
                                dateFormat='dd/mm/yy'
                            />
                        </div>
                        <div className="flex-auto">
                            <Calendar
                                value={endDate}
                                onChange={(e) => setEndDate(e.value)}
                                className="w-full"
                                style={compactControlStyle}
                                inputStyle={compactInputStyle}
                                placeholder={t("Select End Date")}
                                dateFormat='dd/mm/yy'
                            />
                        </div>
                    </div>

                    <div className='flex flex-column justify-content-end'>
                        <Button icon="pi pi-search" label={t("Generate")} outlined iconPos="left" className={compactButtonClass} loading={loading} onClick={handleGenerate} />
                    </div>
                </div>

                {!loading && (
                    <DataTable value={comments} showGridlines emptyMessage={t("No Items")} loading={loading}>
                        <Column header={t("S/N")} body={snBody} style={{ width: '6%' }} />
                        <Column header={t("Incident")} body={incidentSnapshotBody} style={{ width: '34%' }} />
                        <Column header={t("Follow-up")} body={followupAndStatusBody} style={{ width: '40%' }} />
                        <Column header={t("User")} body={userAndDateBody} style={{ width: '20%' }} />
                        <Column header={t("Actions")} body={viewBody} className='w-8rem' />
                    </DataTable>
                )}

                {loading && (
                    <DataTable value={skeletonitems} className="p-datatable-striped">
                        <Column header={t("Follow-up")} body={<Skeleton />} />
                        <Column header={t("Incident")} body={<Skeleton />} />
                        <Column header={t("User")} body={<Skeleton />} />
                        <Column header={t("Status")} body={<Skeleton />} />
                        <Column header={t("Date")} body={<Skeleton />} />
                    </DataTable>
                )}

                <Paginator
                    first={first}
                    rows={rows}
                    totalRecords={totalCount}
                    rowsPerPageOptions={limitOptions}
                    onPageChange={onPageChange}
                    className="mt-3"
                />

                <Dialog visible={viewDialogVisible} modal headerClassName='mb-0 pb-0' style={{ width: '80rem' }} onHide={() => setViewDialogVisible(false)}>
                    <IncidentViewForm
                        setFetcherror={setFetcherror}
                        viewDialogVisible={viewDialogVisible} setViewDialogVisible={setViewDialogVisible}
                        ICNrowData={ICNrowData}
                        incidentValidity={incidentValidity}
                        monitorinitiated={false}
                        handleAllToasts={() => {}}
                        fetchIncidents={() => {}}
                    />
                </Dialog>
            </Card>
        </div>
    )
}

export default FollowupsTable

